import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Mail, Phone, UserRound } from "lucide-react";
import {
  Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle,
} from "Components/ui/sheet";
import { Skeleton } from "Components/ui/skeleton";
import axiosInstance from "../../../redux/api";
import { formatRange, lastSearched, toCsv } from "./searchesView";

// Who searched one word in the page's date range: the signed-in customers by
// name (each opens their customer page), and how many guests. Guests weren't
// signed in, so they can't be named.

const plural = (count, one, many) => `${Number(count || 0).toLocaleString("en-US")} ${count === 1 ? one : many}`;

const CSV_COLUMNS = [
  ["Name", (customer) => customer.name],
  ["Phone", (customer) => customer.phone],
  ["Email", (customer) => customer.email],
  ["Times", (customer) => customer.searches],
  ["Last searched", (customer) => lastSearched(customer.last_searched)],
];

export default function WhoSearched({ term, params, onClose }) {
  const [state, setState] = useState({ status: "loading", data: null, error: "" });
  // Only the answer for the word on screen may land.
  const latest = useRef(0);

  const load = useCallback(() => {
    if (!term) return;
    const request = ++latest.current;
    setState({ status: "loading", data: null, error: "" });
    axiosInstance
      .get("admin/customers/searches/customers", { params: { ...params, term } })
      .then((response) => {
        if (request !== latest.current) return;
        setState({ status: "ready", data: response?.data?.response || null, error: "" });
      })
      .catch((error) => {
        if (request !== latest.current) return;
        setState({ status: "error", data: null, error: error?.response?.data?.message || "Couldn't load who searched this." });
      });
  }, [term, params]);

  useEffect(() => {
    load();
  }, [load]);

  const { status, data, error } = state;
  const customers = data?.customers || [];

  const download = () => {
    const blob = new Blob([toCsv(customers, CSV_COLUMNS)], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `who-searched-${term}-${data.start_date}-to-${data.end_date}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
  };

  return (
    <Sheet open={Boolean(term)} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="right" className="who-searched">
        <SheetHeader>
          <SheetTitle>Who searched <span dir="auto">“{term}”</span></SheetTitle>
          <SheetDescription>
            {status === "ready" && data ? formatRange(data.start_date, data.end_date) : " "}
          </SheetDescription>
        </SheetHeader>

        <SheetBody>
          {status === "loading" && (
            <div data-testid="who-loading" className="d-flex flex-column gap-2">
              {[0, 1, 2].map((key) => <Skeleton key={key} style={{ height: "56px" }} />)}
            </div>
          )}

          {status === "error" && (
            <div role="alert" className="d-flex flex-column gap-2">
              <span style={{ color: "#D14343" }}>{error}</span>
              <button type="button" className="ws-button" onClick={load}>Try again</button>
            </div>
          )}

          {status === "ready" && data && (
            <>
              <p className="ws-summary">
                {plural(data.searches, "search", "searches")}
                {" · "}{plural(customers.length + (data.more_customers || 0), "customer", "customers")}
                {" · "}{plural(data.guests, "guest", "guests")}
              </p>
              {data.guests > 0 && (
                <p className="ws-note">Guests weren't signed in, so they can't be named.</p>
              )}

              {customers.length === 0 ? (
                <p className="ws-empty">No signed-in customer searched this in these dates.</p>
              ) : (
                <ul className="ws-list" aria-label="Customers who searched">
                  {customers.map((customer) => (
                    <li key={customer.id} className="ws-customer">
                      <div className="ws-avatar"><UserRound size={18} /></div>
                      <div className="ws-details">
                        <Link to={`/user/update/${customer.id}`} className="ws-name" dir="auto">{customer.name}</Link>
                        {customer.phone && (
                          <span className="ws-contact"><Phone size={12} /> <span dir="ltr">{customer.phone}</span></span>
                        )}
                        {customer.email && <span className="ws-contact"><Mail size={12} /> {customer.email}</span>}
                      </div>
                      <div className="ws-when">
                        <strong>{plural(customer.searches, "time", "times")}</strong>
                        <span>{lastSearched(customer.last_searched)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {data.more_customers > 0 && (
                <p className="ws-note">+ {plural(data.more_customers, "more customer", "more customers")} not shown.</p>
              )}
            </>
          )}
        </SheetBody>

        {status === "ready" && customers.length > 0 && (
          <SheetFooter>
            <button type="button" className="ws-button" onClick={download}>
              <Download size={14} /> Export customers
            </button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}
